import { alpha } from '@mui/system/colorManipulator';
import {
  MAX_AI_CHAT_CHOICES,
  U21AiChatMultiSelectInnerProps,
} from 'app/shared/u21-ui/components/input/chat/shared';

import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';
import { U21MultiSelect } from 'app/shared/u21-ui/components/input/select/U21MultiSelect';
import { U21TextField } from 'app/shared/u21-ui/components/input/text-field/U21TextField';
import { U21Checkbox } from 'app/shared/u21-ui/components/input/U21Checkbox';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

export const U21AiChatMultiSelectInner = ({
  choices,
  disabled,
  initialValues,
  onSubmit,
}: U21AiChatMultiSelectInnerProps) => {
  const valuesSet = useMemo(() => new Set(initialValues), [initialValues]);
  const choiceSet = useMemo(() => new Set(choices), [choices]);

  // Note: We should always only have a single text input value since we only have a single text input
  // Fallback for having multiple text input values is to join the values with a comma
  const textInputValue = useMemo(
    () => [...valuesSet.difference(choiceSet)].join(', '),
    [valuesSet, choiceSet],
  );

  const [selected, setSelected] = useState<Set<string>>(() =>
    valuesSet.intersection(choiceSet),
  );
  const [customText, setCustomText] = useState(textInputValue);

  const buildAllValues = useCallback(
    (sel: Set<string>, text: string) => [
      ...sel,
      ...(text.trim() ? [text.trim()] : []),
    ],
    [],
  );

  const handleToggle = useCallback((choice: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(choice)) {
        next.delete(choice);
      } else {
        next.add(choice);
      }
      return next;
    });
  }, []);

  const hasValue = useMemo(
    () => selected.size > 0 || customText.trim().length > 0,
    [selected, customText],
  );

  return (
    <>
      {choices.length > MAX_AI_CHAT_CHOICES ? (
        <U21MultiSelect
          disabled={disabled}
          value={Array.from(selected)}
          onChange={(value: string[]) => {
            setSelected(new Set(value));
          }}
          options={choices.map((choice) => ({
            value: choice,
            text: choice,
          }))}
          placeholder="Select options..."
        />
      ) : (
        <U21Spacer spacing={1}>
          {choices.map((choice) => {
            const isSelected = selected.has(choice);
            return (
              <ChoiceRow
                horizontal
                key={choice}
                $selected={isSelected}
                $disabled={disabled}
                onClick={disabled ? undefined : () => handleToggle(choice)}
              >
                <U21Checkbox
                  checked={isSelected}
                  disabled={disabled}
                  label={choice}
                  tabIndex={-1}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleToggle(choice)}
                />
              </ChoiceRow>
            );
          })}
        </U21Spacer>
      )}
      <U21TextField
        type="textarea"
        disabled={disabled}
        value={customText}
        onChange={(value: string) => setCustomText(value)}
        placeholder="Or add something else..."
        minRows={1}
      />
      <StyledSubmitButton
        color="primary"
        variant="contained"
        size="small"
        onClick={() => onSubmit(buildAllValues(selected, customText))}
        disabled={disabled || !hasValue}
      >
        Submit
      </StyledSubmitButton>
    </>
  );
};

const ChoiceRow = styled(U21Spacer)<{
  $selected: boolean;
  $disabled?: boolean;
}>`
  align-items: flex-start;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.primary.main : theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme, $selected, $disabled }) => {
    if ($disabled) {
      return theme.palette.action.disabledBackground;
    }

    if ($selected) {
      return alpha(theme.palette.primary.main, 0.08);
    }

    return theme.palette.background.paper;
  }};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition:
    border-color 0.15s,
    background-color 0.15s;

  &:hover {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? undefined : theme.palette.primary.main};
  }
`;

const StyledSubmitButton = styled(U21Button)`
  width: fit-content;
`;
